import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Layers } from 'lucide-react';
import { embeddingProviderService, type EmbeddingProvider } from '../../../components/api/embeddingProviderService';
import AddEmbeddingProviderModal from './embeddings/AddEmbeddingProviderModal';
import VaultEntityCard from './DataVault/VaultEntityCard';

const Embeddings: React.FC<{ isLight?: boolean; requestAdd?: number }> = ({ isLight = false, requestAdd = 0 }) => {
  const [list, setList] = useState<EmbeddingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editProvider, setEditProvider] = useState<EmbeddingProvider | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toggleProvider, setToggleProvider] = useState<EmbeddingProvider | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await embeddingProviderService.getList();
      setList(Array.isArray(res) ? res : []);
    } catch {
      setError('Failed to load embedding providers.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const lastAddTick = useRef(requestAdd);
  useEffect(() => {
    if (requestAdd === lastAddTick.current) return;
    lastAddTick.current = requestAdd;
    if (requestAdd < 1) return;
    setEditProvider(null);
    setShowModal(true);
  }, [requestAdd]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditProvider(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await embeddingProviderService.delete(deleteId);
      await fetchList();
      setDeleteId(null);
    } catch {
      setError('Failed to delete provider.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleConfirm = async () => {
    if (!toggleProvider) return;
    const p = toggleProvider;
    setTogglingId(p.id);
    setToggleProvider(null);
    try {
      await embeddingProviderService.setActive(p.id, !p.isActive);
      await fetchList();
    } catch {
      setError('Failed to update status.');
    } finally {
      setTogglingId(null);
    }
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const openEdit = (provider: EmbeddingProvider) => {
    setEditProvider(provider);
    setShowModal(true);
  };

  return (
    <div>
      {error ? <p className="mb-3 text-sm text-red-400">{error}</p> : null}

      {loading ? (
        <div className={`text-center py-10 text-sm ${muted}`}>Loading…</div>
      ) : list.length === 0 ? (
        <div className={`text-center py-10 text-sm ${muted}`}>No embedding providers yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((p) => {
            const details = [
              p.name,
              p.defaultModel,
              p.defaultVectorDimensions != null ? `${p.defaultVectorDimensions} dim` : null,
              p.apiVersion,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <VaultEntityCard
                key={p.id}
                isLight={isLight}
                title={p.displayName || p.name}
                description={details || p.description || 'No description provided.'}
                tag={togglingId === p.id ? '…' : p.isActive ? 'Active' : 'Inactive'}
                tagTitle={p.isActive ? 'Set inactive' : 'Set active'}
                icon={Layers}
                iconClass={isLight ? 'bg-sky-50 text-sky-600' : 'bg-sky-500/15 text-sky-300'}
                inactive={!p.isActive}
                onOpen={() => openEdit(p)}
                onEdit={() => openEdit(p)}
                onDelete={() => setDeleteId(p.id)}
                onTagClick={togglingId === p.id ? undefined : () => setToggleProvider(p)}
              />
            );
          })}
        </div>
      )}

      {showModal && (
        <AddEmbeddingProviderModal
          onClose={handleCloseModal}
          onSubmit={fetchList}
          initialData={editProvider}
        />
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Delete embedding provider?</h3>
            <p className="text-gray-400 text-sm mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="text-gray-400 hover:text-white text-sm" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleProvider && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">
              {toggleProvider.isActive ? 'Set provider to inactive?' : 'Set provider to active?'}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {toggleProvider.isActive ? 'This provider will be disabled.' : 'This provider will be enabled.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setToggleProvider(null)}
                className="text-gray-400 hover:text-white text-sm"
                disabled={togglingId === toggleProvider.id}
              >
                Cancel
              </button>
              <button
                onClick={handleToggleConfirm}
                className={toggleProvider.isActive ? 'bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50' : 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50'}
                disabled={togglingId === toggleProvider.id}
              >
                {togglingId === toggleProvider.id ? 'Updating…' : toggleProvider.isActive ? 'Yes, set to Inactive' : 'Yes, set to Active'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Embeddings;

